import React, { useState, useRef, useCallback, useEffect } from 'react';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Popover } from '../popover/Popover.js';
import { ArrowDown, Enter, Esc } from '../../utils/keycode.js';
import { isFunction } from '../../utils/is.js';

const Trigger = props => {
  const {
    children,
    theme,
    clickOutsideToClose = true,
    content,
    dataTestId,
    fallbackPlacements,
    injectionContainer,
    offset = [0, 8],
    onClose,
    onCloseDelay = 150,
    onOpen,
    onOpenDelay = 150,
    popupVisible,
    position,
    springConfig,
    trigger = 'hover',
    updatePopover,
    useAnchorWidth = false,
    withArrow = true,
    zIndex,
    disabled,
    onClickOutside
  } = props;
  const [visible, setVisible] = useState(popupVisible);
  const [target, setTarget] = useState(null);
  const contentRef = useRef(null);
  const timer = useRef(0);
  const springConfigMerged = springConfig || {
    from: {
      opacity: 0,
      config: {
        duration: 150
      }
    },
    to: {
      opacity: 1,
      config: {
        duration: 150
      }
    },
    leave: {
      opacity: 0,
      config: {
        duration: 150
      }
    }
  };
  const getChild = () => {
    const child = children;
    if (['string', 'number'].indexOf(typeof children) > -1 || React.Children.count(children) > 1) {
      return /*#__PURE__*/jsx("span", {
        ref: setTarget,
        children: children
      });
    }
    return child;
  };
  const triggerPropsEvent = (eventName, e) => {
    const child = getChild();
    const childHandler = child && child.props && child.props[eventName];
    if (isFunction(childHandler)) {
      childHandler(e);
    }
    if (isFunction(props[eventName])) {
      props[eventName](e);
    }
  };
  const open = () => {
    if (!visible) {
      setVisible(true);
      if (onOpen) {
        onOpen();
      }
    }
  };
  const close = useCallback(() => {
    if (visible) {
      setVisible(false);
      if (onClose) {
        onClose();
      }
    }
  }, [onClose, visible]);
  const toggle = () => {
    if (visible) {
      close();
    } else {
      open();
    }
  };
  const clickedOutside = useCallback(node => {
    // onClickOutside && onClickOutside()
    if (target && target.contains(node)) {
      return false;
    }
    return !(contentRef.current && contentRef.current.contains(node));
  }, [target, onClickOutside]);
  useEffect(() => {
    if (clickOutsideToClose) {
      const handleBodyClick = event => {
        const eventTarget = event.target;
        if (clickedOutside(eventTarget)) {
          close();
        }
      };
      document.body.addEventListener('click', handleBodyClick);
      return () => {
        document.body.removeEventListener('click', handleBodyClick);
        clearTimeout(timer.current);
      };
    }
    return undefined;
  }, [clickedOutside, close, clickOutsideToClose]);
  useEffect(() => {
    setVisible(cur => {
      return popupVisible !== cur ? popupVisible : cur;
    });
  }, [popupVisible]);
  const handleTargetClick = e => {
    if (e?.target?.value && e?.target?.value !== '') {
      open();
      return;
    }
    toggle();
  };
  const onKeyDown = e => {
    triggerPropsEvent('onKeyDown', e);
    const keyCode = e.code;
    switch (keyCode) {
      case Esc.key:
        close();
        break;
      case Enter.key:
        toggle();
        break;
      case ArrowDown.key:
        open();
        break;
    }
    return e;
  };
  const handleMouseOver = () => {
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      open();
    }, onOpenDelay);
  };
  const handleMouseOut = () => {
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      close();
    }, onCloseDelay);
  };
  const handleTouchStart = event => {
    const eventTarget = event.target;
    clearTimeout(timer.current);
    if (clickedOutside(eventTarget)) {
      timer.current = window.setTimeout(() => {
        close();
      }, onCloseDelay);
    } else {
      open();
    }
  };
  const onClickProps = {
    onClick: handleTargetClick
  };
  const onHoverProps = {
    onMouseOver: handleMouseOver,
    onMouseOut: handleMouseOut,
    onTouchStart: handleTouchStart
  };
  const getTargetProps = () => {
    switch (trigger) {
      case 'click':
        return {
          onKeyDown: e => {
            onKeyDown(e);
          },
          ...onClickProps
        };
      case 'hover':
        return {
          ...onHoverProps
        };
      default:
        return {};
    }
  };
  const getContentProps = () => {
    const props = {
      ref: contentRef,
      'data-test-id': dataTestId,
      className: ''
    };
    if (trigger === 'hover') {
      return {
        ...props,
        ...onHoverProps
      };
    }
    return props;
  };
  const handledChildren = () => {
    const child = getChild();
    return /*#__PURE__*/React.cloneElement(child, {
      ...child.props,
      ...getTargetProps(),
      ref: data => {
        if (data) {
          const {
            refObject
          } = data;
          setTarget(refObject || data);
        }
      }
    });
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [handledChildren(), content && !disabled ? /*#__PURE__*/jsx(Popover, {
      ref: contentRef,
      anchorElement: target,
      isOpen: visible,
      useAnchorWidth: useAnchorWidth,
      injectionContainer: injectionContainer,
      theme: theme,
      offset: offset,
      withArrow: withArrow,
      position: position,
      update: updatePopover,
      zIndex: zIndex,
      dataTestId: dataTestId,
      fallbackPlacements: fallbackPlacements,
      springConfig: springConfigMerged,
      children: /*#__PURE__*/jsx("div", {
        ...getContentProps(),
        children: content
      })
    }) : null]
  });
};

export { Trigger };
